/**
 * chatApiService - REST API calls for AI chat.
 *
 * Pure async service module - no React, no WebSocket dependencies.
 * All calls require a pre-obtained Firebase ID token from the caller.
 */

import { fetchWithRetry } from '../../../lib/apiClient';
import type { ChatSessionSummary } from '../state/chatState';
import type { HistoryMessage } from './historyHydrator';

// ─────────────────────────────────────────────────────────────────────────────
// Scope
// ─────────────────────────────────────────────────────────────────────────────

export type ChatScope =
  | { type: 'project'; projectUuid: string }
  | { type: 'workspace'; workspaceUuid: string };

function scopeBasePath(scope: ChatScope): string {
  return scope.type === 'project'
    ? `/api/project/${scope.projectUuid}`
    : `/api/workspace/${scope.workspaceUuid}`;
}

// ─────────────────────────────────────────────────────────────────────────────
// API functions
// ─────────────────────────────────────────────────────────────────────────────

export async function apiListSessions(
  scope: ChatScope,
  idToken: string
): Promise<ChatSessionSummary[]> {
  const url = `${scopeBasePath(scope)}/chat/sessions`;
  const res = await fetchWithRetry(url, { headers: { Authorization: `Bearer ${idToken}` } });
  if (!res.ok) throw new Error(`list_sessions_failed:${res.status}`);
  const data = (await res.json()) as { sessions: ChatSessionSummary[] };
  return data.sessions;
}

export async function apiCreateSession(
  scope: ChatScope,
  payload: { title?: string | null; mode?: string },
  idToken: string
): Promise<ChatSessionSummary> {
  const url = `${scopeBasePath(scope)}/chat/sessions`;
  const res = await fetchWithRetry(url, {
    method: 'POST',
    headers: { Authorization: `Bearer ${idToken}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error(`create_session_failed:${res.status}`);
  const data = (await res.json()) as { session: ChatSessionSummary };
  return data.session;
}

export async function apiLoadSessionHistory(
  scope: ChatScope,
  sessionUuid: string,
  idToken: string,
  opts?: { limit?: number; before?: number | null }
): Promise<{ messages: HistoryMessage[]; hasMore: boolean }> {
  const limit = opts?.limit ?? 50;
  let url = `${scopeBasePath(scope)}/chat/sessions/${sessionUuid}/history?limit=${limit}`;
  if (opts?.before != null) url += `&before=${opts.before}`;
  const res = await fetchWithRetry(url, { headers: { Authorization: `Bearer ${idToken}` } });
  if (!res.ok) throw new Error(`load_history_failed:${res.status}`);
  return res.json() as Promise<{ messages: HistoryMessage[]; hasMore: boolean }>;
}

export async function apiRenameSession(
  scope: ChatScope,
  sessionUuid: string,
  title: string,
  idToken: string
): Promise<string> {
  const url = `${scopeBasePath(scope)}/chat/sessions/${sessionUuid}`;
  const res = await fetchWithRetry(url, {
    method: 'PATCH',
    headers: { Authorization: `Bearer ${idToken}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ title }),
  });
  if (!res.ok) throw new Error(`rename_session_failed:${res.status}`);
  const data = (await res.json()) as { title: string };
  return data.title;
}

export async function apiArchiveSession(
  scope: ChatScope,
  sessionUuid: string,
  idToken: string
): Promise<void> {
  const url = `${scopeBasePath(scope)}/chat/sessions/${sessionUuid}/archive`;
  const res = await fetchWithRetry(url, {
    method: 'POST',
    headers: { Authorization: `Bearer ${idToken}` },
  });
  if (!res.ok) throw new Error(`archive_session_failed:${res.status}`);
}

export async function apiSetMessageActionResult(
  scope: ChatScope,
  sessionUuid: string,
  messageUuid: string,
  actionResult: 'accepted' | 'dismissed',
  idToken: string,
  acceptedFieldKeys?: string[],
  cardKey?: string
): Promise<void> {
  const url = `${scopeBasePath(scope)}/chat/sessions/${sessionUuid}/messages/${messageUuid}/action-result`;
  const res = await fetchWithRetry(url, {
    method: 'PATCH',
    headers: { Authorization: `Bearer ${idToken}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ actionResult, acceptedFieldKeys, cardKey }),
  });
  if (!res.ok) throw new Error(`set_action_result_failed:${res.status}`);
}
